import React, { useEffect, useRef, useState } from 'react';
import { Snackbar, Typography, Stack, Box, Fade, IconButton } from '@mui/material';
import { ErrorOutlineRounded, Close } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackError } from '../../Reducers/snackErrors';

const AUTO_HIDE_MS = 6000;

const ErrorSnack = () => {
  const dispatch = useDispatch();
  const title = useSelector((state) => state.errors.title);
  const body  = useSelector((state) => state.errors.body);

  const [progress, setProgress] = useState(100);
  const intervalRef = useRef(null);
  const open = title != null;

  useEffect(() => {
    if (!open) return;
    setProgress(100);

    const step = 100 / (AUTO_HIDE_MS / 100);
    intervalRef.current = setInterval(() => {
      setProgress(prev => {
        if (prev <= 0) { clearInterval(intervalRef.current); return 0; }
        return prev - step;
      });
    }, 100);

    return () => clearInterval(intervalRef.current);
  }, [open, title]);

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') return;
    clearInterval(intervalRef.current);
    dispatch(setSnackError({ title: null, body: null }));
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={AUTO_HIDE_MS}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={Fade}
      sx={{ '&.MuiSnackbar-root': { bottom: { xs: 16, sm: 24 }, right: { xs: 16, sm: 24 } } }}
    >
      <Box
        sx={{
          width: 360,
          bgcolor: 'background.paper',
          border: '0.5px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ px: 1.75, pt: 1.75, pb: 1.5 }}>
          {/* Icon */}
          <Box sx={{
            width: 32, height: 32, borderRadius: '50%',
            bgcolor: 'error.50',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <ErrorOutlineRounded sx={{ fontSize: 17, color: 'error.main' }} />
          </Box>

          {/* Text */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" fontWeight={500} noWrap>{title}</Typography>
            {body && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.5 }}>
                {body}
              </Typography>
            )}
          </Box>

          {/* Close */}
          <IconButton size="small" onClick={handleClose} sx={{ mt: '-2px', color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}>
            <Close sx={{ fontSize: 15 }} />
          </IconButton>
        </Stack>

        {/* Progress bar */}
        <Box sx={{ height: 3, bgcolor: 'action.hover' }}>
          <Box
            sx={{
              height: '100%',
              width: `${progress}%`,
              bgcolor: 'error.main',
              borderRadius: '0 2px 2px 0',
              transition: 'width 0.1s linear',
            }}
          />
        </Box>
      </Box>
    </Snackbar>
  );
};

export default ErrorSnack;